import { useState } from "react";

import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { FormMode } from "@/Common/Models/UI/Enums";
import { EmployeeListComponent } from "./EmployeeListComponent";

import styles from "./UsersListFieldComponent.module.scss";

interface IUsersListFieldComponentProps {
    dataCy: string;
    selectedEmployeesList: Person[];
    searchableEmployees: Immutable.List<SearchableItemModelT<Person>>;
    setSelectedEmployees: (list: Person[]) => void;
    mode: FormMode;
    className?: string;
    onRemoveUsers?: (usersToRemove: Person[], onSuccess: () => void) => void;
}

function UsersListFieldComponent(props: Readonly<IUsersListFieldComponentProps>): JSX.Element {
    const { getText } = useTranslation();
    const [searchText, setSearchText] = useState<string>();
    const { searchableEmployees, selectedEmployeesList, setSelectedEmployees: setSelectedEmployee } = props;

    const handleEmployeeSelected = (_, person: Person): void => {
        setSearchText("");
        setSelectedEmployee([person, ...selectedEmployeesList]);
    };

    const handleRemoveUsers = (usersToRemove: Person[], nextSelectedEmployees: Person[]): void => {
        if (props.onRemoveUsers)
            props.onRemoveUsers(usersToRemove, () => setSelectedEmployee(nextSelectedEmployees));
        else
            setSelectedEmployee(nextSelectedEmployees);
    };

    const handleRemoveAllUsers = (): void => {
        handleRemoveUsers(selectedEmployeesList, []);
    };

    const handleRemoveOneUser = (userToRemove: Person): void => {
        const nextSelectedEmployees = selectedEmployeesList?.filter(employee => employee.ContactFacetId !== userToRemove.ContactFacetId);
        handleRemoveUsers([userToRemove], nextSelectedEmployees);
    };

    return (
        <div className={`${styles.employeeSearchContainer} ${props.className ?? ""}`}>
            {props.mode === FormMode.Edit && (
                <>
                    <TextboxControl
                        className={styles.addEmployeeSearchInput}
                        debounceMs={200}
                        value={searchText}
                        autoCompleteItems={searchableEmployees}
                        onAutoCompleteSelectCallBack={handleEmployeeSelected}
                        onAutoCompleteSelectClearTextBox
                        listWidth="100%"
                        placeholder={getText("SearchLabel")}
                        onChange={searchText => setSearchText(searchText)}
                        valid
                        dataCy={`${props.dataCy}-search-input`}
                    />

                    {selectedEmployeesList?.length > 0 && (
                        <button
                            data-cy={`${props.dataCy}-remove-all-users`}
                            className={styles.removeAllUsersLink}
                            onClick={handleRemoveAllUsers}
                        >
                            {getText("Allowlist_RemoveAllUsersLabel")}
                        </button>

                    )}
                </>
            )}

            <EmployeeListComponent
                onRemoveEmployeeClick={handleRemoveOneUser}
                employees={selectedEmployeesList}
                showDeleteIcons={props.mode == FormMode.Edit}
                dataCy={props.dataCy}
            />
        </div>
    );
}

export { UsersListFieldComponent };
